import React from 'react';
import {
  Box,
  Select,
  SelectTrigger,
  SelectIcon,
  SelectPortal,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicatorWrapper,
  SelectDragIndicator,
  SelectItem,
  Badge,
  BadgeText,
  Spinner,
  Text,
  ChevronDownIcon,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import type { ModuleLecturerResponseVersionHistory } from '@/common/api/models/moduleLecturerResponseVersionHistory';

import { getVersionSelectorLabels } from './versionSelectorLabels';

interface VersionSelectorProps {
  versionHistory?: ModuleLecturerResponseVersionHistory | null;
  selectedVersion: string | null;
  onVersionChange: (version: string) => void;
  isLoading?: boolean;
}

/**
 * A presentational component that displays a version selector dropdown.
 */
const VersionSelector: React.FC<VersionSelectorProps> = ({
  versionHistory = {},
  selectedVersion,
  onVersionChange,
  isLoading = false,
}) => {
  const { t } = useTranslation();

  // Show loading state
  if (isLoading) {
    return (
      <Badge size="sm" variant="outline" action="info" borderRadius="$full">
        <BadgeText>{t('common.labels.version', { version: selectedVersion })}</BadgeText>
        <Spinner size="small" ml="$1" />
      </Badge>
    );
  }

  // If no versions or only one version, just show the badge
  if (!versionHistory || Object.keys(versionHistory).length <= 1) {
    return (
      <Badge size="sm" variant="outline" action="info" borderRadius="$full">
        <BadgeText>{t('common.labels.version', { version: selectedVersion })}</BadgeText>
      </Badge>
    );
  }

  // Format version options for the dropdown and separate by workflow state
  interface VersionOption {
    value: string;
    label: string;
    triggerLabel: string;
    state: string;
    isRetired: boolean;
  }

  const activeVersions: VersionOption[] = [];
  const retiredVersions: VersionOption[] = [];

  const historyEntries = Array.isArray(versionHistory) ? versionHistory : Object.values(versionHistory || {});

  historyEntries.forEach((entry) => {
    const { triggerLabel, menuLabel } = getVersionSelectorLabels(
      {
        version: entry.version,
        workflowState: entry?.workflowState,
        sourceVersion: entry?.sourceVersion,
      },
      t,
    );

    const option: VersionOption = {
      value: entry.version,
      label: menuLabel,
      triggerLabel,
      state: entry?.workflowState || 'draft',
      isRetired: entry?.workflowState === 'retired',
    };

    if (option.isRetired) {
      retiredVersions.push(option);
    } else {
      activeVersions.push(option);
    }
  });

  const sortVersions = (a: VersionOption, b: VersionOption) => {
    const aParts = a.value.split('.').map(Number);
    const bParts = b.value.split('.').map(Number);

    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aVal = aParts[i] || 0;
      const bVal = bParts[i] || 0;
      if (aVal !== bVal) return bVal - aVal;
    }
    return 0;
  };

  activeVersions.sort(sortVersions);
  retiredVersions.sort(sortVersions);

  const allOptions = [...activeVersions, ...retiredVersions];
  const selectedOption = allOptions.find((option) => option.value === selectedVersion);
  const triggerLabel = selectedOption?.triggerLabel ?? selectedVersion ?? '';

  return (
    <Box minWidth={240}>
      <Select
        selectedValue={selectedVersion || ''}
        onValueChange={(value) => onVersionChange(value)}
      >
        <SelectTrigger size="sm" variant="outline" bg="$bgCard">
          <Text
            flex={1}
            numberOfLines={1}
            fontSize={13}
            color="$textHeading"
            fontWeight="medium"
            px="$3"
          >
            {triggerLabel}
          </Text>
          <SelectIcon as={ChevronDownIcon} size="sm" mr="$2" color="$textSubtle" />
        </SelectTrigger>
        <SelectPortal>
          <SelectBackdrop />
          <SelectContent>
            <SelectDragIndicatorWrapper>
              <SelectDragIndicator />
            </SelectDragIndicatorWrapper>

            {/* Active versions */}
            {activeVersions.map((option) => (
              <SelectItem
                key={option.value}
                label={option.label}
                value={option.value}
              />
            ))}

            {retiredVersions.map((option) => (
              <SelectItem
                key={option.value}
                label={option.label}
                value={option.value}
              />
            ))}
          </SelectContent>
        </SelectPortal>
      </Select>
    </Box>
  );
};

export default VersionSelector;
